import { useRef, useCallback } from 'react';

import { AutoScroller, Cursor, Feedback, PointerSensor, PreventSelection } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { DragIndicator } from '@mui/icons-material';
import { Alert, AlertTitle, Box } from '@mui/material';

import Database from '@/assets/icons/Database';
import ClearAllButton from '@/components/ClearAllButton';
import { InfoBox } from '@/components/InfoBox';
import Typography from '@/components/Typography';
import { EmptyState } from '@/features/Dashboard/form/AppForm/components/EmptyState';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { AppType } from '@/features/Dashboard/types';
import getAmountLabel from '@/utils/getAmountLabel';

import { DroppableArea } from './DroppableArea';

import type { DragDropContainerProps } from './types';

const sensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return [source.element, source.handle];
    }
  })
];

const DragDropContainer: React.FC<DragDropContainerProps> = ({
  itemsName = 'item',
  maxCapacity,
  setOpen
}) => {
  const { form, setCanPreview } = useAppForm();
  const container = useRef<HTMLDivElement>(null);
  const hadSymbolsBefore = useRef(false);
  const handleClearAllClick = useCallback(() => {
    form.setFieldValue('symbols', { active: [], backlog: [] });
  }, [form]);

  return (
    <form.Subscribe selector={s => ({ symbols: s.values?.symbols, type: s.values?.type })}>
      {({ symbols, type }) => {
        // Track if we ever had symbols to determine if we should show the empty alert
        if (symbols && symbols.active.length > 0) {
          hadSymbolsBefore.current = true;
        }

        // Compute widgetType without using hooks inside a render-prop
        let widgetType: string;
        switch (type) {
          case AppType.Tiles:
            widgetType = 'highlighted price';
            break;
          case AppType.Table:
            widgetType = 'data table';
            break;
          default:
            widgetType = 'chart';
        }

        if (!symbols) return null;

        const isMaxCapacityReached =
          symbols.backlog.length > 0 || symbols.active.length > maxCapacity;

        return (
          <DragDropProvider
            plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
            sensors={sensors}
            onDragEnd={() => {
              setCanPreview(true);
              const currentSymbols = form.state.values?.symbols;
              if (!currentSymbols) return;

              const { active, backlog } = currentSymbols;

              // Remove items from active list if max capacity is reached
              if (active.length > maxCapacity) {
                const itemsToRemove = active.slice(maxCapacity);
                const newActive = active.slice(0, maxCapacity);

                form.setFieldValue('symbols', {
                  active: newActive,
                  backlog: [...itemsToRemove, ...backlog]
                });
              } else {
                // add items to active list if max capacity is not reached
                const itemsToAdd = backlog.slice(0, maxCapacity - active.length);
                const newBacklog = backlog.slice(maxCapacity - active.length);
                form.setFieldValue('symbols', {
                  active: [...active, ...itemsToAdd],
                  backlog: newBacklog
                });
              }
            }}
            onDragOver={event => {
              const currentSymbols = form.state.values?.symbols;
              if (!currentSymbols) return;

              const newSymbols = move(currentSymbols, event);
              form.setFieldValue('symbols', newSymbols);
            }}
            onDragStart={() => {
              setCanPreview(false);
            }}
          >
            <Box>
              {symbols.active.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                      fontSize: '14px'
                    }}
                  >
                    <Typography size={14}>
                      Selected: {symbols.active.length + symbols.backlog.length}
                      {symbols.backlog.length > 0 &&
                        ` (${symbols.active.length} displayed, ${symbols.backlog.length} not displayed)`}
                    </Typography>
                    <ClearAllButton
                      sx={{ fontSize: '14px' }}
                      onClick={handleClearAllClick}
                    />
                  </Box>
                  {isMaxCapacityReached && (
                    <InfoBox>
                      <Alert
                        severity='info'
                        sx={{
                          '& .MuiAlert-icon': {
                            color: 'primary.main',
                            paddingTop: 0.5
                          },
                          backgroundColor: 'inherit',
                          color: 'inherit',
                          mb: 3
                        }}
                      >
                        <AlertTitle
                          sx={{
                            fontSize: '12px',
                            mb: '8px'
                          }}
                        >
                          App display limit: {getAmountLabel(maxCapacity, itemsName)}
                        </AlertTitle>
                        <Typography
                          sx={{
                            fontSize: '12px'
                          }}
                        >
                          Only the first {getAmountLabel(maxCapacity, itemsName)} will be displayed
                          on the {widgetType}. Items beyond this limit can be reordered by dragging
                          them to higher positions.
                        </Typography>
                      </Alert>
                    </InfoBox>
                  )}
                </>
              )}

              {symbols.active.length === 0 && (
                <>
                  <Typography
                    size={14}
                    sx={{ marginBottom: 2 }}
                  >
                    Selected: 0
                  </Typography>
                  {hadSymbolsBefore.current && (
                    <InfoBox>
                      <Alert
                        severity='error'
                        sx={{
                          backgroundColor: 'inherit',
                          color: 'inherit',
                          py: 1,
                          mb: 3,
                          '& .MuiAlert-icon': {
                            color: 'primary.main',
                            paddingTop: 0.5
                          }
                        }}
                      >
                        <AlertTitle
                          sx={{
                            fontSize: '12px',
                            mb: '8px'
                          }}
                        >
                          No {itemsName} selected
                        </AlertTitle>
                        <Typography
                          sx={{
                            fontSize: '12px'
                          }}
                        >
                          You need to add at least one {itemsName} to display this app.
                        </Typography>
                      </Alert>
                    </InfoBox>
                  )}
                  <EmptyState
                    description='Browse the catalog to select items'
                    icon={<Database sx={{ fontSize: '24px' }} />}
                    setOpen={setOpen}
                    title='No items selected'
                  />
                </>
              )}
              <div ref={container}>
                {symbols.active.length > 0 && (
                  <>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'row', fontSize: 14, fontWeight: 500 }}
                    >
                      <Box
                        sx={{
                          width: 100,
                          color: 'text.disabled',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <DragIndicator
                          sx={{
                            height: '16px',
                            width: '16px'
                          }}
                        />
                        Drag to reorder
                      </Box>
                      <Box sx={{ width: 100, ml: 2, mr: 1 }}>Alias</Box>
                      <Box>Short Title</Box>
                    </Box>
                    <DroppableArea
                      container={container}
                      id='active'
                      items={symbols.active}
                    />
                  </>
                )}
                {isMaxCapacityReached && (
                  <>
                    <Box sx={{ my: 3, display: 'flex', position: 'relative' }}>
                      <Box
                        sx={theme => ({
                          flex: 1,
                          height: 2,
                          backgroundRepeat: 'repeat-x',
                          backgroundImage: `repeating-linear-gradient(to right,  ${theme.palette.primary.main} 0 16px, transparent 16px 32px)`,
                          backgroundSize: 'auto 2px',
                          backgroundPosition: 'center'
                        })}
                      />
                      <Typography
                        sx={{
                          px: 2,
                          position: 'absolute',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'background.paper'
                        }}
                      >
                        App display limit: {getAmountLabel(maxCapacity, itemsName)}
                      </Typography>
                    </Box>

                    <DroppableArea
                      baseIndex={symbols.active.length}
                      container={container}
                      id='backlog'
                      items={symbols.backlog}
                      sx={{
                        '& [data-sortable-item]': {
                          borderColor: '#6C737A',
                          color: '#6C737A'
                        }
                      }}
                    />
                  </>
                )}
              </div>
            </Box>
          </DragDropProvider>
        );
      }}
    </form.Subscribe>
  );
};

export default DragDropContainer;
